import api from './api';
import ApiClient from './apiClient';
import { State } from './locationService';

export type InternalShipmentStatus =
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'in_transit'
  | 'delivered'
  | 'cancelled';

export interface PaginationMeta {
  current_page: number;
  from: number;
  last_page: number;
  per_page: number;
  to: number;
  total: number;
}

export interface PaginatedApiResponse<T> {
  status: string;
  message?: string;
  data: T[];
  meta: PaginationMeta;
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
}

export interface ApiResponse<T> {
  status: string;
  message?: string;
  data: T;
}

export interface ShipmentContactSnapshot {
  id?: number | string;
  nickname?: string;
  contactName?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  country_id?: number | null;
  state_id?: number | null;
  city_id?: number | null;
  city_other?: string;
  street?: string;
  houseNo?: string;
  zipCode?: string;
  deliveryDetails?: string;
  cityName?: string;
  country?: {
    id: number;
    name: string;
    iso2?: string;
  } | null;
  state?: {
    id: number;
    name: string;
    state_code?: string;
  } | null;
  city?: {
    id: number;
    name: string;
  } | null;
}

export interface InternalShipment {
  id: number;
  shipmentNumber: string;
  userId: number;
  status: InternalShipmentStatus;
  statusLabel: string;
  canEdit: boolean;
  canCancel: boolean;
  senderContactId: number | null;
  recipientContactId: number | null;
  recipientStateId: number;
  sender: ShipmentContactSnapshot;
  recipient: ShipmentContactSnapshot;
  recipientState?: State;
  itemDescription: string;
  weightKg: number;
  declaredValue: number | null;
  quotedCost: number;
  currency: string;
  adminNotes?: string | null;
  carrier?: string | null;
  trackingNumber?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  inTransitAt?: string | null;
  deliveredAt?: string | null;
  cancelledAt?: string | null;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    fullName: string;
    email: string;
  };
}

export interface InternalShipmentQuote {
  stateId: number;
  weightKg: number;
  quotedCost: number;
  currency: string;
  rateCardId: number;
  tier: {
    id: number;
    minWeightKg: number;
    maxWeightKg: number | null;
    price: number;
    label: string;
  };
}

export interface InternalShipmentPayload {
  senderContactId: number;
  recipientContactId: number;
  itemDescription: string;
  weightKg: number;
  declaredValue?: number | null;
  currency?: string;
}

export interface InternalShipmentFilters {
  search?: string;
  status?: InternalShipmentStatus | 'all';
  page?: number;
  per_page?: number;
  user_id?: number;
}

export interface InternalShipmentStats {
  totalShipments: number;
  pendingReview: number;
  approved: number;
  rejected: number;
  inTransit: number;
  delivered: number;
  cancelled: number;
  totalQuotedRevenue: number;
}

export interface InternalShipmentRateTier {
  id?: number;
  sortOrder: number;
  minWeightKg: number;
  maxWeightKg: number | null;
  price: number;
}

export interface InternalShipmentRateCard {
  id: number;
  stateId: number;
  isActive: boolean;
  notes?: string | null;
  state?: State;
  tiers: InternalShipmentRateTier[];
}

export interface InternalShipmentRateCardDetail {
  state: State;
  rateCard: InternalShipmentRateCard | null;
  defaultTiers: Array<InternalShipmentRateTier & { label: string }>;
}

export interface InternalShipmentRateImportFailure {
  rows: number[];
  state: string;
  message: string;
}

export interface InternalShipmentRateImportResult {
  summary: {
    processedRows: number;
    processedStates: number;
    successfulStates: number;
    failedStates: number;
  };
  failures: InternalShipmentRateImportFailure[];
}

export interface InternalShipmentRateCloneTarget {
  state: State;
  action: 'create' | 'update';
  actionLabel: string;
  currentStatus: 'active' | 'inactive' | 'not_configured';
  currentRateCardId: number | null;
  clonedRateCardId?: number | null;
}

export interface InternalShipmentRateCloneSummary {
  targetCount: number;
  createCount: number;
  updateCount: number;
}

export interface InternalShipmentRateClonePreview {
  source: {
    state: State;
    rateCard: InternalShipmentRateCard;
  };
  targets: InternalShipmentRateCloneTarget[];
  summary: InternalShipmentRateCloneSummary;
}

const getInternalShipments = (filters: InternalShipmentFilters = {}) => {
  const params = {
    ...filters,
    status: filters.status === 'all' ? undefined : filters.status,
  };

  return ApiClient.get<PaginatedApiResponse<InternalShipment>>('/internal-shipments', params);
};

const getInternalShipment = (id: string | number) => {
  return ApiClient.get<ApiResponse<InternalShipment>>(`/internal-shipments/${id}`);
};

const quoteInternalShipment = (payload: { state_id: number; weightKg: number; currency?: string }) => {
  return ApiClient.post<ApiResponse<InternalShipmentQuote>, typeof payload>('/internal-shipments/quote', payload);
};

const createInternalShipment = (payload: InternalShipmentPayload) => {
  return ApiClient.post<ApiResponse<InternalShipment>, InternalShipmentPayload>('/internal-shipments', payload);
};

const updateInternalShipment = (id: string | number, payload: InternalShipmentPayload) => {
  return ApiClient.put<ApiResponse<InternalShipment>, InternalShipmentPayload>(`/internal-shipments/${id}`, payload);
};

const cancelInternalShipment = (id: string | number) => {
  return ApiClient.post<ApiResponse<InternalShipment>, Record<string, never>>(`/internal-shipments/${id}/cancel`, {});
};

const downloadInternalShipmentPdf = async (id: string | number): Promise<Blob> => {
  const response = await api.get(`/internal-shipments/${id}/pdf`, {
    responseType: 'blob',
  });

  return response.data;
};

const getAdminInternalShipments = (filters: InternalShipmentFilters = {}) => {
  const params = {
    ...filters,
    status: filters.status === 'all' ? undefined : filters.status,
  };

  return ApiClient.get<PaginatedApiResponse<InternalShipment>>('/admin/internal-shipments', params);
};

const getAdminInternalShipment = (id: string | number) => {
  return ApiClient.get<ApiResponse<InternalShipment>>(`/admin/internal-shipments/${id}`);
};

const getAdminInternalShipmentStats = (filters: InternalShipmentFilters = {}) => {
  return ApiClient.get<ApiResponse<InternalShipmentStats>>('/admin/internal-shipments/stats', filters);
};

const updateAdminInternalShipmentStatus = (
  id: string | number,
  payload: { status: Exclude<InternalShipmentStatus, 'pending_review' | 'cancelled'>; adminNotes?: string }
) => {
  return ApiClient.patch<ApiResponse<InternalShipment>, typeof payload>(`/admin/internal-shipments/${id}/status`, payload);
};

const updateAdminInternalShipmentTracking = (
  id: string | number,
  payload: { carrier: string; trackingNumber: string; adminNotes?: string }
) => {
  return ApiClient.patch<ApiResponse<InternalShipment>, typeof payload>(`/admin/internal-shipments/${id}/tracking`, payload);
};

const downloadAdminInternalShipmentPdf = async (id: string | number): Promise<Blob> => {
  const response = await api.get(`/admin/internal-shipments/${id}/pdf`, {
    responseType: 'blob',
  });

  return response.data;
};

const getInternalShipmentRateCards = (params?: { search?: string; state_id?: number; country_id?: number; per_page?: number; page?: number }) => {
  return ApiClient.get<PaginatedApiResponse<InternalShipmentRateCard>>('/admin/internal-shipment-rates', params);
};

const getInternalShipmentRateCard = (stateId: number) => {
  return ApiClient.get<ApiResponse<InternalShipmentRateCardDetail>>(`/admin/internal-shipment-rates/${stateId}`);
};

const upsertInternalShipmentRateCard = (
  stateId: number,
  payload: { isActive: boolean; notes?: string; tiers: InternalShipmentRateTier[] }
) => {
  return ApiClient.put<ApiResponse<InternalShipmentRateCard>, typeof payload>(`/admin/internal-shipment-rates/${stateId}`, payload);
};

const importInternalShipmentRates = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post<ApiResponse<InternalShipmentRateImportResult>>('/admin/internal-shipment-rates/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

const downloadInternalShipmentRatesExport = async (): Promise<Blob> => {
  const response = await api.get('/admin/internal-shipment-rates/export', {
    responseType: 'blob',
  });

  return response.data;
};

const downloadInternalShipmentRatesTemplate = async (): Promise<Blob> => {
  const response = await api.get('/admin/internal-shipment-rates/template', {
    responseType: 'blob',
  });

  return response.data;
};

const previewInternalShipmentRateClone = (payload: { sourceStateId: number; targetStateIds: number[] }) => {
  return ApiClient.post<ApiResponse<InternalShipmentRateClonePreview>, typeof payload>('/admin/internal-shipment-rates/clone-preview', payload);
};

const cloneInternalShipmentRates = (payload: { sourceStateId: number; targetStateIds: number[] }) => {
  return ApiClient.post<ApiResponse<InternalShipmentRateClonePreview>, typeof payload>('/admin/internal-shipment-rates/clone', payload);
};

const internalShipmentService = {
  getInternalShipments,
  getInternalShipment,
  quoteInternalShipment,
  createInternalShipment,
  updateInternalShipment,
  cancelInternalShipment,
  downloadInternalShipmentPdf,
  getAdminInternalShipments,
  getAdminInternalShipment,
  getAdminInternalShipmentStats,
  updateAdminInternalShipmentStatus,
  updateAdminInternalShipmentTracking,
  downloadAdminInternalShipmentPdf,
  getInternalShipmentRateCards,
  getInternalShipmentRateCard,
  upsertInternalShipmentRateCard,
  importInternalShipmentRates,
  downloadInternalShipmentRatesExport,
  downloadInternalShipmentRatesTemplate,
  previewInternalShipmentRateClone,
  cloneInternalShipmentRates,
};

export default internalShipmentService;
